// I'm not really happy about the filename

/* 
    Species richness means counting the number of different species that are found in an observation.
    (aka the length of the datapoint)
*/
export function calcSpeciesRichness(data: number[]): number {
  return data.filter((value) => value > 0).length
}

/**
 *  The Shannon index was presented in 1948 by Claude Shannon and was originally used in communication theory
In the field of biodiversity research it takes into account both the species richness of an observation and the abundance of the different species
The Shannon index is a measure that quantifies the uncertainty in predicting the correct species of any random individual taken from the observation
The Shannon index gives more importance to less abundant species
 * @param data 
 * @returns 
 */
export function calcShannonIndex(data: number[]): number {
  const total = data.reduce((sum, value) => sum + value, 0)
  if (total === 0) return 0

  const proportions = data.map((value) => value / total)
  return -proportions.reduce((sum, p) => sum + (p > 0 ? p * Math.log(p) : 0), 0)
}

/**
 * The Simpson index was presented in 1949 by Edward Simpson and was originally used to measure the concentration level of different species within an observation
In the field of biodiversity research it takes into account both the species richness of an observation and the abundance of the different species
The Simpson index is a measure that quantifies the probability that two random indidividuals taken from the observation are of the same species
The Simpson index gives more importance to dominant species
 * @param data 
 * @returns 
 */
export function calcSimpsonIndex(data: number[]): number {
  const total = data.reduce((sum, value) => sum + value, 0)
  if (total === 0) return 0

  const proportions = data.map((value) => value / total)
  return proportions.reduce((sum, p) => sum + p * p, 0)
}

/**
 * Instead of the Simpson index, most often used is the Gini-Simpson index which is the inverse of the original Simpson index (!)
 * @param data
 * @returns
 */
export function calcGiniSimpsonIndex(data: number[]): number {
  return 1 - calcSimpsonIndex(data)
}

/**
 * The Absolute Effective Diversity (AED) was presented in 2020 by Gatti, Amoroso & Monaco and intends to be "the first reliable and comparable measure, which combines both the absolute richness and the evenness [...] in a unique, simple and comprehensive numerical value"
It is strongly related to the biodiversity indices of Chao1 (developed by Anne Chao, 1984), species richness, Shannon and Simpson
By combining the canonical biodiversity indices the AED "has been shown to be reliable and less affected by estimation errors than the three traditional diversity measures taken alone"
Also the AED "allows not only a calculation of the effective diversity but also of the absolute diversity of a community in a single universal metric" (Source)
Source: https://robertocazzollagatti.files.wordpress.com/2020/04/estimating-absolute-effective-biodiversity.pdf
 * @param data 
 * @returns 
 */
export function calcAbsoluteEffectiveDiversity(data: number[]): number {
  const N = calcSpeciesRichness(data) //species richness
  const H_1 = calcShannonIndex(data) // Shannon index
  const D = calcSimpsonIndex(data) // Simpson index

  return N + 0.5 * Math.exp(H_1) ** 2 * D
}
